import { CheckedState } from '@radix-ui/react-checkbox';
import { type ClassValue, clsx } from 'clsx';
import React, {useEffect, useState} from 'react';
import { twMerge } from 'tailwind-merge';
import { LanguageCode } from '@app/types';
import i18next from 'i18next';
import { getLanguageFromI18n } from '@app/hooks/context/LanguageContext';
import {RouteParams, RoutePaths} from "@gen/routes";
import {useTranslation} from "react-i18next";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function Log({ args }: { args: unknown[] | unknown }) {
    if (!Array.isArray(args)) args = [args];
    console.log(...(args as []));
    return null;
}

export function getChildrenByComponent(children: React.ReactNode, displayName: string) {
    const matched: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;

        const childType = child.type as React.FC<unknown>;

        const props:
            | {
            children?: React.ReactNode;
        }
            | undefined = child.props as
            | {
            children?: React.ReactNode;
        }
            | undefined;
        if ((childType.displayName || childType.name) === displayName) {
            matched.push(child);
        } else if (props?.children) {
            matched.push(...getChildrenByComponent(props?.children, displayName));
        }
    });

    return matched;
}

export function checkedStateToBoolean(state: CheckedState): boolean {
    return state === 'indeterminate' ? false : state;
}

export function randomId(length = 7, prefix?: string) {
    if (length > 36) length = 36;

    return (prefix ?? "") + Math.random().toString(36).substring(length);
}

/**
 * Array or Single Value to Array (AOS)
 * Ensures the input value is always returned as an array.
 *
 * This function takes either a single value or an array of values as input.
 * If the provided value is not an array, it wraps the value into an array.
 * Returns the resulting array.
 *
 * @template T
 * @param {T | T[]} value - A single value or an array of values.
 * @returns {T[]} An array containing the input value(s).
 */
export const aos: <T>(value: T|T[]) => T[] = (value) => {
    if (!Array.isArray(value)) {
        value = [value];
    }
    return value;
}

/**
 * Represents a type that can be either a single instance of type `T` or an array of instances of type `T`.
 *
 * This type is useful in scenarios where a value may come as a single item or as a collection of items,
 * providing flexibility in handling both cases seamlessly.
 *
 * @template T The type of the element(s).
 */
export type AOS<T> = T | T[];

export const inlineTrans = (translations: Partial<Record<LanguageCode, string>>) => {
    const language = getLanguageFromI18n(i18next.language);
    return translations[language] ?? translations.default ?? "";
}

export type StrictOmit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export const buildUrl = (baseUrl: string, sub: string) => {
    if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
    if (sub.startsWith('/')) sub = sub.slice(1);
    return `${baseUrl}/${sub}`;
}

export type RouteParamsInfer<Href extends RoutePaths | `${RoutePaths}?${string}`> = [RouteParams[Href extends `${infer P}?${string}` ? P : Href]] extends [never] ? [] : [params: RouteParams[Href extends `${infer P}?${string}` ? P : Href]];

export function route<Href extends RoutePaths | `${RoutePaths}?${string}`>(
    href: Href,
    ...params: RouteParamsInfer<Href>
): string {
    const paramsObj = (params as any[])[0] as Record<string, any> | undefined;
    const resolved = String(href);

    if (!paramsObj) return resolved;

    return resolved.replace(/:(\w+)/g, (_, key) => {
        const val = paramsObj?.[key];
        return val === undefined || val === null ? "" : String(val);
    });
}

type AddSlash<T extends string> = T extends `/${string}` ? T : `/${T}`;
type AddEndSlash<T extends string> = T extends `${string}/` ? T : `${T}/`;
type Slashed<T extends string> = AddEndSlash<AddSlash<T>>;
type S<T extends string, R extends string[] = []> = T extends `/${infer seg}/${infer Rest}` ? [...S<Slashed<Rest>, R>, seg] : R;

type Params<P extends string[], R extends string[] = []> = P extends [infer First, ...infer Rest extends string[]] ? (First extends `:${infer FirstWithout}` ? Params<Rest, [...R, FirstWithout]> : Params<Rest, R>) : R;


export function anyRoute<Href extends string>(
    href: Href,
    ...args: Params<S<Slashed<Href>>> extends [] ? [] : [{
        [K in Params<S<Slashed<Href>>>[number]]: string
    }]
): string {
    if (args.length === 0) return href;
    const params = args[0];
    return href.replace(/:(\w+)/g, (_, key) => (params as any)[key]);
}

export function queue(fn: () => void) {
    setTimeout(fn, 0);
}

export const ensureJsonString = (data: string|undefined|unknown): string => {
    return data ? (typeof data !== "string" ? "{}" : data) : "{}";
}

type NestedKeys<T> = NonNullable<T> extends Date | readonly unknown[] ? never : Keys<NonNullable<T>>;
export type Keys<T> = T extends object ? {
    [K in keyof T & string]: K | (NestedKeys<T[K]> extends never ? never : `${K}.${NestedKeys<T[K]>}`);
}[keyof T & string] : never;

export type GetValue<K extends string, D> = K extends `${infer Key}.${infer Rest}` ? Key extends keyof D ? GetValue<Rest, D[Key]> : never : K extends keyof D ? D[K] : never;

export const getFirstStringOnTranslatedObject = (translation: Record<string, string>|string) => {
    if (typeof translation === "string") {
        return translation;
    }

    const languages = Object.keys(translation);
    return translation['default'] ?? translation[languages[0]] ?? "";
}

export function useDebounce<T>(value: T, delay = 300) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export function debounce<F extends (this: any, ...args: any) => any>(func: F, delay = 300) {
    return debounceWithControl(func, delay).fn;
}

export function debounceWithControl<F extends (this: any, ...args: any) => any>(func: F, delay = 300) {
    const timeoutData = {
        value: undefined! as NodeJS.Timeout,
    };

    const fn = function(this: ThisParameterType<F>, ...args: Parameters<F>) {
        clearTimeout(timeoutData.value);
        timeoutData.value = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
    return {
        fn,
        cancel: () => clearTimeout(timeoutData.value)
    };
}


export type MaybePromise<T> = T | Promise<T>;

export function nothingFactoryIf<T extends (...args: any) => any>(fn: T, condition: boolean|(() => boolean), fallback?: T): T {
    if (typeof condition === "function") condition = condition();
    if (!condition) return fn;
    return fallback ?? (() => {}) as T
}

export type ValueOrSetter<T> = T | ((value: T) => T);

export function logAndReturn<T>(value: T, key?: string) {
    if (key) {
        console.log(key, value);
    }
    else console.log(value);
    return value;
}

type ColumnTitleSource = { columnDef: { meta?: { labelKey?: string } } };

export const getColumnTitle = (column: ColumnTitleSource, titleKey: string, t: ReturnType<typeof useTranslation>['t']) => {
    const labelKey = column.columnDef.meta?.labelKey;
    if (labelKey) return t(labelKey as never);

    const words = titleKey.replaceAll('_', ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    const fallback = words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
    return t(`components.data-table.columns.${titleKey}` as never, t(titleKey as never, fallback));
}
